import 'filepond/dist/filepond.min.css';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { QueryObserverResult } from 'react-query';
import { useSelector } from 'react-redux';
import { Button } from 'src/components/atoms';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import { handleErrorToast } from 'src/components/molecules/Toast/Toast';
import { CaseDocumentsTable } from 'src/components/moleculesLegacy';
import { DocumentEditor } from 'src/components/moleculesLegacy/DocumentEditor';
import { ConfirmDialog } from 'src/components/organismsLegacy';
import {
  CancelButton,
  ConfirmButton,
} from 'src/components/organismsLegacy/ConfirmDialog/ConfirmDialog.styles';
import { casePageVm } from 'src/components/pages/Case/Case.slice';
import { CaseByIdQuery, CaseDocumentationType } from 'src/graphql';
import { UseCaseDocuments } from 'src/hooks/cases';
import { ALL_MIME_TYPES, SaveDocumentButtonStates } from 'src/types';
import {
  formatValidationError,
  validateFileUpload,
} from 'src/utils/validateFileUpload/fileValidation';
import {
  BrowseText,
  ButtonsContainer,
  DocumentHeaderContainer,
  DocumentsContainer,
  DragDropLabel,
  DragDropText,
  FilePondContainer,
  FileSizeLimitText,
  RotatingSpinner,
} from './CaseDocuments.styles';

interface Props {
  caseId: string;
  documents: CaseDocumentationType[];
  caseDocuments: Omit<
    UseCaseDocuments,
    'activeTab' | 'setTempActiveTab' | 'selectActiveTab'
  >;
  isSaving: boolean;
  setIsSaving: (isSaving: boolean) => void;
  refetch: () => Promise<QueryObserverResult<CaseByIdQuery, unknown>>;
  disableLegacyStyles?: boolean;
}

const handleFileProcessing = async (
  files: File[],
  caseDocuments: Omit<
    UseCaseDocuments,
    'activeTab' | 'setTempActiveTab' | 'selectActiveTab'
  >,
  setInvalidFileNames: (fileNames: string[]) => void,
  setIsUploading: (uploading: boolean) => void,
  refetch: () => Promise<QueryObserverResult<CaseByIdQuery, unknown>>,
) => {
  const validFiles: File[] = [];
  const invalidFiles: string[] = [];

  for (const file of files) {
    try {
      validateFileUpload({
        filename: file.name,
        mimeType: file.type,
      });
      validFiles.push(file);
    } catch (error) {
      invalidFiles.push(file.name);
    }
  }

  if (invalidFiles.length > 0) {
    setInvalidFileNames(invalidFiles);
  }

  if (validFiles.length > 0) {
    setIsUploading(true);
    for (const file of validFiles) {
      await caseDocuments.updateFile(
        file.name,
        file,
        {},
        () => {},
        () => {},
        () => {}, // progress
        () => {}, // abort
        (_transferId: string) => {}, // transfer
        {
          // options
          chunkTransferId: '',
          chunkServer: { url: '' },
          chunkUploads: false,
          chunkForce: false,
          chunkSize: 0,
          chunkRetryDelays: [],
        },
      );
    }
    setIsUploading(false);
    await refetch();
  }
};

export const CaseDocuments: FC<Props> = ({
  caseId,
  documents,
  caseDocuments,
  isSaving,
  setIsSaving,
  refetch,
  disableLegacyStyles,
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [invalidFileNames, setInvalidFileNames] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const reduxCase = useSelector(casePageVm);

  useEffect(() => {
    if (invalidFileNames.length > 0) {
      handleErrorToast({
        toastMessage: 'Unsupported file type',
        toastDescription: formatValidationError(invalidFileNames),
      });
      setInvalidFileNames([]);
    }
  }, [invalidFileNames]);

  const userHasReadOnlyAccess = useMemo(
    () =>
      reduxCase.permissions.canViewCaseWorkspaceTab &&
      !reduxCase.permissions.canEditCaseWorkspaceTab,
    [
      reduxCase.permissions.canEditCaseWorkspaceTab,
      reduxCase.permissions.canViewCaseWorkspaceTab,
    ],
  );

  useEffect(() => {
    if (caseDocuments.updateCaseDocumentation.isLoading) {
      caseDocuments.setDocumentState(SaveDocumentButtonStates.SAVING);
    }
  }, [caseDocuments, caseDocuments.updateCaseDocumentation.isLoading]);

  if (caseDocuments.createDocumentEditorOpen) {
    if (caseDocuments.documentIsLoading) {
      return <p>Loading...</p>;
    }
    return (
      <DocumentEditor
        caseId={caseId}
        caseDocuments={caseDocuments}
        isSaving={isSaving}
        setIsSaving={setIsSaving}
        refetch={refetch}
        disableLegacyStyles={disableLegacyStyles}
      />
    );
  }

  return (
    <DocumentsContainer
      $disableLegacyStyles={disableLegacyStyles}
      data-testid="documents-container"
    >
      {!userHasReadOnlyAccess && (
        <DocumentHeaderContainer>
          <FilePondContainer>
            {/* Drag-and-drop and click-to-upload area */}
            <DragDropLabel
              data-testid="drag-drop-label"
              htmlFor="case-documents-upload-input"
              tabIndex={0}
              onClick={() => {
                fileInputRef.current?.click();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  fileInputRef.current?.click();
                }
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={async (e) => {
                e.preventDefault();
                const droppedFiles = Array.from(e.dataTransfer.files);
                await handleFileProcessing(
                  droppedFiles,
                  caseDocuments,
                  setInvalidFileNames,
                  setIsUploading,
                  refetch,
                );
              }}
            >
              <DragDropText>
                Drop files here or{' '}
                <BrowseText
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  browse files
                </BrowseText>
              </DragDropText>
              <FileSizeLimitText>Each file size limit: 100MB</FileSizeLimitText>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ALL_MIME_TYPES.join(',')}
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const selectedFiles = Array.from(e.target.files || []);
                  await handleFileProcessing(
                    selectedFiles,
                    caseDocuments,
                    setInvalidFileNames,
                    setIsUploading,
                    refetch,
                  );
                }}
              />
            </DragDropLabel>
            {isUploading && <RotatingSpinner />}
          </FilePondContainer>
          <ButtonsContainer>
            <Button
              variant="primary"
              size="medium"
              data-testid="new-document-button"
              onClick={() => {
                caseDocuments.setDocumentState(
                  SaveDocumentButtonStates.UNSAVED,
                );
                caseDocuments.setCreateDocumentEditorOpen(true);
              }}
            >
              <>
                <StyledIconInButton
                  size="medium"
                  position="left"
                  className="uil uil-plus"
                />
                New
              </>
            </Button>
            <Button
              variant="primary"
              size="medium"
              data-testid="remove-document-button"
              onClick={() => setShowConfirmDialog(true)}
              disabled={caseDocuments.selectedDocuments.length === 0}
            >
              <>
                <StyledIconInButton
                  size="medium"
                  position="left"
                  className="uil uil-trash-alt"
                />
                Delete
              </>
            </Button>
          </ButtonsContainer>
        </DocumentHeaderContainer>
      )}
      <ConfirmDialog
        open={showConfirmDialog}
        setOpen={setShowConfirmDialog}
        title="Remove document(s)?"
        body="Are you sure you want to remove the document? You will not be able to recover the document after"
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={() => {
          caseDocuments.handleRemoveDocuments();
          setShowConfirmDialog(false);
        }}
        data-testid="cancel-remove-document-confirm"
        confirmButton={
          <ConfirmButton
            onClick={() => {
              caseDocuments.handleRemoveDocuments();
              setShowConfirmDialog(false);
            }}
            data-testid="confirm-remove-documents"
          >
            Continue
          </ConfirmButton>
        }
        cancelButton={
          <CancelButton
            onClick={() => setShowConfirmDialog(false)}
            data-testid="cancel-remove-documents"
          >
            Cancel
          </CancelButton>
        }
      />
      <CaseDocumentsTable
        documents={documents}
        selectedDocuments={caseDocuments.selectedDocuments ?? []}
        setSelectedDocuments={caseDocuments.setSelectedDocuments}
        openDocument={caseDocuments.handleOpenDocument}
      />
    </DocumentsContainer>
  );
};
